import React from 'react';
import { SafeSEO } from '@/components/seo/SafeSEO';
import Navigation from '../components/Navigation';
import { SEO_ROUTES } from '@/seo/seo-routes';
import Footer from '../components/Footer';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { UploadCloud, ArrowRight } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

const MAX_ATTACHMENTS = 5;
const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const supportSchema = z.object({
  fullName: z
    .string()
    .min(2, 'Please enter your full name.'),
  email: z
    .string()
    .min(1, 'Email is required.')
    .email('Enter a valid email address.'),
  category: z
    .string()
    .min(1, 'Select an issue category.'),
  priority: z
    .string()
    .min(1, 'Select a priority level.'),
  subject: z
    .string()
    .min(6, 'Subject should be at least 6 characters.')
    .max(120, 'Subject should be under 120 characters.'),
  description: z
    .string()
    .min(30, 'Please provide a bit more detail so we can help quickly.')
    .max(2000, 'Description must be under 2000 characters.'),
  attachments: z.array(z.any()).optional(),
  includeSystemInfo: z.boolean().default(true),
});

type SupportTicketFormValues = z.infer<typeof supportSchema>;

const categoryOptions = [
  { value: 'account-access', label: "I can't log in or access my account" },
  { value: 'safety-question', label: 'Product question about Joey Family safety features' },
  { value: 'billing', label: 'Billing or subscription question' },
  { value: 'bug-report', label: 'Something is not working as expected' },
  { value: 'sales', label: 'Talk to sales about Joey Family' },
  { value: 'other', label: 'Something else' },
];

const priorityOptions = [
  { value: 'low', label: 'Low - FYI or general question' },
  { value: 'normal', label: 'Normal - needs help soon' },
  { value: 'high', label: 'High - blocking my family' },
  { value: 'urgent', label: 'Urgent - safety concern' },
];

const ContactUs: React.FC = () => {
  const { toast } = useToast();

  const form = useForm<SupportTicketFormValues>({
    resolver: zodResolver(supportSchema),
    defaultValues: {
      fullName: '',
      email: '',
      category: '',
      priority: '',
      subject: '',
      description: '',
      attachments: [],
      includeSystemInfo: true,
    },
  });

  const onSubmit = async (values: SupportTicketFormValues) => {
    form.clearErrors('attachments');

    if (values.attachments && values.attachments.length > MAX_ATTACHMENTS) {
      form.setError('attachments', {
        type: 'manual',
        message: `Please upload up to ${MAX_ATTACHMENTS} files.`,
      });
      return;
    }

    if (
      values.attachments &&
      values.attachments.some((file: File) => file.size > MAX_FILE_SIZE_BYTES)
    ) {
      form.setError('attachments', {
        type: 'manual',
        message: `Each file must be smaller than ${MAX_FILE_SIZE_MB}MB.`,
      });
      return;
    }

    try {
      // Integrate with support ticket endpoint + reCAPTCHA when ready.
      console.debug('Support ticket submitted', {
        ...values,
        attachments: values.attachments?.map((file: File) => ({
          name: file.name,
          size: file.size,
          type: file.type,
        })),
      });

      toast({
        title: 'Support request sent',
        description: 'Thanks! Our support team will reply within one business day.',
      });
      form.reset({
        fullName: '',
        email: '',
        category: '',
        priority: '',
        subject: '',
        description: '',
        attachments: [],
        includeSystemInfo: true,
      });
    } catch (error) {
      toast({
        title: 'Something went wrong',
        description: 'We could not send your request. Please try again or email support@joeyfamily.com.',
        variant: 'destructive',
      });
      console.error('Failed to submit support ticket', error);
    }
  };

  const handleFileChange = (
    files: FileList | null,
    onChange: (value: File[]) => void,
  ) => {
    const nextFiles = Array.from(files ?? []);
    onChange(nextFiles);
  };

  return (
    <>
      <SafeSEO
        title="Contact Joey Support"
        description="Get help with setup, billing and safety questions. Our team can guide you on alerts for bullying, risky contacts and money asks."
        url={SEO_ROUTES.contact}
        image="/img/og/open_graph.png"
      />
      <div className="min-h-screen bg-background">
      <Navigation />
      
      <main className="pt-44 md:pt-56 pb-24 px-6 max-md:px-4">
        <section className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-hero font-medium text-joey-cyan mb-6 max-md:text-title">
              Submit a Support Ticket
            </h1>
            <p className="text-subtitle text-muted-foreground max-md:text-body">
              Locked out of your account or have questions about Joey Family? Tell us what&apos;s happening and our support team will respond within one business day.
            </p>
          </div>

          <Card className="border-none shadow-xl">
            <CardHeader className="space-y-2 border-b border-gray-100">
              <CardTitle className="text-2xl text-brand-navy flex items-center gap-2">
                <UploadCloud className="h-6 w-6 text-joey-cyan" aria-hidden="true" />
                Send us the details
              </CardTitle>
              <CardDescription className="text-base text-slate-600">
                The more information you share, the faster we can help. Attach screenshots if you have them. We&rsquo;ll add reCAPTCHA verification during backend integration.
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-8">
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="fullName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Full Name</FormLabel>
                          <FormControl>
                            <Input placeholder="Your full name" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email Address</FormLabel>
                          <FormControl>
                            <Input type="email" placeholder="you@example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      control={form.control}
                      name="category"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Issue Category</FormLabel>
                          <FormControl>
                            <select
                              className={cn(
                                'flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-joey-cyan focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                              )}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            >
                              <option value="" disabled>
                                Select a category
                              </option>
                              {categoryOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="priority"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Priority</FormLabel>
                          <FormControl>
                            <select
                              className={cn(
                                'flex h-11 w-full rounded-lg border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-joey-cyan focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50'
                              )}
                              value={field.value}
                              onChange={field.onChange}
                              onBlur={field.onBlur}
                            >
                              <option value="" disabled>
                                Select a priority
                              </option>
                              {priorityOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="subject"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subject</FormLabel>
                        <FormControl>
                          <Input placeholder="Brief description of the issue" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Detailed Description</FormLabel>
                        <FormControl>
                          <Textarea
                            rows={6}
                            placeholder="Please include steps to reproduce the issue, the Joey feature you were using, and any recent changes to your device."
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="attachments"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Screenshots (optional)</FormLabel>
                          <FormDescription className="text-xs text-muted-foreground">
                            PNG, JPG, GIF - up to {MAX_ATTACHMENTS} files - {MAX_FILE_SIZE_MB}MB each
                          </FormDescription>
                        </div>
                        <FormControl>
                          <label
                            className={cn(
                              'flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-joey-cyan/50 bg-joey-cyan/5 px-6 py-10 text-center transition hover:border-joey-cyan hover:bg-joey-cyan/10 cursor-pointer'
                            )}
                          >
                            <UploadCloud className="h-8 w-8 text-joey-cyan" aria-hidden="true" />
                            <span className="text-sm font-medium text-joey-cyan">
                              Drop screenshots here, or click to browse
                            </span>
                            <span className="text-xs text-muted-foreground">
                              We&rsquo;ll automatically include filenames with your ticket.
                            </span>
                            <input
                              type="file"
                              className="hidden"
                              multiple
                              accept=".png,.jpg,.jpeg,.gif"
                              onChange={(event) => {
                                handleFileChange(event.target.files, field.onChange);
                                if (event.target) {
                                  event.target.value = '';
                                }
                              }}
                              onBlur={field.onBlur}
                            />
                          </label>
                        </FormControl>
                        {field.value && field.value.length > 0 && (
                          <ul className="mt-4 space-y-1 text-sm text-slate-600">
                            {field.value.map((file: File) => (
                              <li key={`${file.name}-${file.lastModified}`}>
                                {file.name} &bull; {(file.size / 1024 / 1024).toFixed(2)}MB
                              </li>
                            ))}
                          </ul>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="includeSystemInfo"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-xl border border-slate-200 bg-slate-50 p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(!!checked)}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-snug">
                          <FormLabel className="text-sm font-medium">
                            Include basic system information with my ticket
                          </FormLabel>
                          <FormDescription className="text-xs text-muted-foreground">
                            Helpful for debugging account access issues. No personal content is included.
                          </FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />

                  <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <p className="text-sm text-muted-foreground">
                      Joey Family is owned by Laneway Analytics Pty Ltd. Support hours: Monday-Friday, 9am-5pm AEST.
                    </p>
                    <Button
                      type="submit"
                      className="px-8 py-6 text-base font-semibold"
                      disabled={form.formState.isSubmitting}
                    >
                      {form.formState.isSubmitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Submit Support Ticket
                          <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </section>
      </main>
      
      <Footer />
    </div>
    </>
  );
};

export default ContactUs;

